import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cookie, Cake, Crown, Star, Trash2, Pencil, UserPlus, RotateCcw, Heart, Volume2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SoundPicker from "@/components/SoundPicker";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        <Icon className="w-4 h-4" style={{ color }} /> {label}
      </div>
      <div className="text-3xl font-800 font-heading mt-2" style={{ color }}>{value}</div>
    </div>
  );
}

function EditDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(user);
  useEffect(() => setForm(user), [user]);
  const save = async () => {
    await api.post("/users", {
      username: form.username,
      cookies: parseInt(form.cookies) || 0,
      cupcakes: parseInt(form.cupcakes) || 0,
      sprinkles: parseInt(form.sprinkles) || 0,
      is_vip: form.is_vip,
      is_special: form.is_special,
      is_mod: form.is_mod,
      nickname: form.nickname,
      family_role: form.family_role || "none",
      walk_on_message: form.walk_on_message,
      walk_on_sound: form.walk_on_sound || "",
      stream_title: form.stream_title,
      game: form.game,
      stream_url: form.stream_url,
      special_title: form.special_title || "",
      special_reaction: form.special_reaction || "",
    });
    toast.success(`Saved ${form.username}`);
    setOpen(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-8 hover:text-primary" data-testid={`edit-user-${user.username}`}>
          <Pencil className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit @{user.username}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">🍪 Cookies</Label>
              <Input type="number" value={form.cookies} onChange={(e) => setForm({ ...form, cookies: e.target.value })} data-testid="edit-cookies" className="bg-secondary border-white/10 mt-1" /></div>
            <div><Label className="text-xs">🧁 Cupcakes</Label>
              <Input type="number" value={form.cupcakes} onChange={(e) => setForm({ ...form, cupcakes: e.target.value })} data-testid="edit-cupcakes" className="bg-secondary border-white/10 mt-1" /></div>
          </div>
          <div><Label className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3 text-purple-400" /> ✨ Sprinkles (RPG Idle-Battler currency)</Label>
            <Input type="number" value={form.sprinkles ?? 0} onChange={(e) => setForm({ ...form, sprinkles: e.target.value })} data-testid="edit-sprinkles" className="bg-secondary border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nickname</Label>
              <Input value={form.nickname || ""} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="what PancakeCupCake_ calls them" data-testid="edit-nickname" className="bg-secondary border-white/10 mt-1" /></div>
            <div><Label className="text-xs flex items-center gap-1"><Heart className="w-3 h-3 text-primary" /> Family role</Label>
              <Select value={form.family_role || "none"} onValueChange={(v) => setForm({ ...form, family_role: v })}>
                <SelectTrigger data-testid="edit-family-role" className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="mother">Mother 👑</SelectItem>
                  <SelectItem value="father">Father 🐼</SelectItem>
                  <SelectItem value="uncle">Uncle</SelectItem>
                  <SelectItem value="aunt">Aunt</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
            <div><Label className="text-sm flex items-center gap-2"><Crown className="w-4 h-4 text-cookie" /> VIP (Walk-on)</Label>
              <p className="text-[11px] text-muted-foreground">Gets an entrance announcement</p></div>
            <Switch checked={form.is_vip} onCheckedChange={(v) => setForm({ ...form, is_vip: v })} data-testid="edit-vip" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
            <div><Label className="text-sm flex items-center gap-2"><Star className="w-4 h-4 text-primary" /> Special user</Label>
              <p className="text-[11px] text-muted-foreground">Gets special command outcomes</p></div>
            <Switch checked={form.is_special} onCheckedChange={(v) => setForm({ ...form, is_special: v })} data-testid="edit-special" />
          </div>
          <div className="rounded-lg bg-secondary px-4 py-3 space-y-2">
            <Label className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3 text-cookie" /> Custom effect (any person, add as many as you like)</Label>
            <Input value={form.special_title || ""} onChange={(e) => setForm({ ...form, special_title: e.target.value })} placeholder="Their title (e.g. big brother, bestie)" data-testid="edit-special-title" className="bg-[#0a0a0a] border-white/10" />
            <Input value={form.special_reaction || ""} onChange={(e) => setForm({ ...form, special_reaction: e.target.value })} placeholder="Her !hug reaction for them — use {user} for their name" data-testid="edit-special-reaction" className="bg-[#0a0a0a] border-white/10" />
            <p className="text-[10px] text-muted-foreground">Set this on each person you want a unique reaction for — every user has their own.</p>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
            <div><Label className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-400" /> Moderator</Label>
              <p className="text-[11px] text-muted-foreground">Can use !mood to change her mood</p></div>
            <Switch checked={form.is_mod} onCheckedChange={(v) => setForm({ ...form, is_mod: v })} data-testid="edit-mod" />
          </div>
          <div><Label className="text-xs">Walk-on message</Label>
            <Textarea value={form.walk_on_message || ""} onChange={(e) => setForm({ ...form, walk_on_message: e.target.value })} data-testid="edit-walkon" className="bg-secondary border-white/10 mt-1" placeholder="🎺 Everyone welcome @user!" /></div>
          <div><Label className="text-xs flex items-center gap-1"><Volume2 className="w-3 h-3 text-primary" /> Walk-on sound (plays on overlay)</Label>
            <div className="mt-1"><SoundPicker value={form.walk_on_sound} onChange={(v) => setForm({ ...form, walk_on_sound: v })} testid="edit-walkon-sound" /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Stream title (for !so)</Label>
              <Input value={form.stream_title || ""} onChange={(e) => setForm({ ...form, stream_title: e.target.value })} data-testid="edit-title" className="bg-secondary border-white/10 mt-1" /></div>
            <div><Label className="text-xs">Game</Label>
              <Input value={form.game || ""} onChange={(e) => setForm({ ...form, game: e.target.value })} data-testid="edit-game" className="bg-secondary border-white/10 mt-1" /></div>
          </div>
          <div><Label className="text-xs">Stream URL</Label>
            <Input value={form.stream_url || ""} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} data-testid="edit-url" className="bg-secondary border-white/10 mt-1" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="save-user-button" className="bg-primary hover:bg-primary/90">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Economy() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState("");
  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const totalCookies = users.reduce((s, u) => s + (u.cookies || 0), 0);
  const totalCupcakes = users.reduce((s, u) => s + (u.cupcakes || 0), 0);
  const totalSprinkles = users.reduce((s, u) => s + (u.sprinkles || 0), 0);

  const add = async () => {
    if (!newUser.trim()) return;
    await api.post("/users", { username: newUser });
    setNewUser("");
    load();
    toast.success("Viewer added");
  };
  const del = async (u) => { await api.delete(`/users/${u.username}`); load(); toast.success("Removed"); };
  const resetSession = async () => { await api.post("/users/reset-session"); toast.success("Walk-ons reset for new session"); };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-800 font-heading">Economy</h1>
          <p className="text-muted-foreground mt-1 text-sm">Cookies, cupcakes & ✨ Sprinkles for every viewer profile.</p>
        </div>
        <Button variant="outline" onClick={resetSession} data-testid="reset-session-button" className="border-white/10 rounded-lg">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset Session Walk-ons
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Cookie} label="Total Cookies" value={totalCookies} color="#FFB703" />
        <StatCard icon={Cake} label="Total Cupcakes" value={totalCupcakes} color="#FF3366" />
        <StatCard icon={Sparkles} label="Total Sprinkles" value={totalSprinkles} color="#a855f7" />
        <StatCard icon={Crown} label="VIPs" value={users.filter((u) => u.is_vip).length} color="#FFB703" />
        <StatCard icon={Star} label="Viewers" value={users.length} color="#FF3366" />
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-4 flex gap-2">
        <Input value={newUser} onChange={(e) => setNewUser(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a viewer by name" className="bg-secondary border-white/10 focus:ring-primary" data-testid="new-user-input" />
        <Button onClick={add} data-testid="add-user-button" className="bg-primary hover:bg-primary/90 rounded-lg"><UserPlus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-white/10 text-xs">
              <th className="px-4 py-3">Viewer</th>
              <th className="px-4 py-3">🍪 Cookies</th>
              <th className="px-4 py-3">🧁 Cupcakes</th>
              <th className="px-4 py-3">✨ Sprinkles</th>
              <th className="px-4 py-3">Flags</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 font-medium">
                  @{u.username}
                  {u.nickname && <span className="block text-xs text-muted-foreground">“{u.nickname}”</span>}
                </td>
                <td className="px-4 py-3 text-cookie font-semibold">{u.cookies}</td>
                <td className="px-4 py-3 text-primary font-semibold">{u.cupcakes}</td>
                <td className="px-4 py-3 font-semibold" style={{ color: "#a855f7" }}>{u.sprinkles || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {u.family_role && u.family_role !== "none" && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-300 flex items-center gap-1"><Heart className="w-3 h-3" />{u.family_role}</span>
                    )}
                    {u.is_vip && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cookie/20 text-cookie flex items-center gap-1"><Crown className="w-3 h-3" />VIP</span>}
                    {u.is_special && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 text-primary flex items-center gap-1"><Star className="w-3 h-3" />Special</span>}
                    {u.is_mod && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Mod</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <EditDialog user={u} onSaved={load} />
                    <Button size="sm" variant="ghost" onClick={() => del(u)} data-testid={`delete-user-${u.username}`} className="h-8 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
